/**
 * PremiumPackageItem
 * Flutter source: wallet_screen.dart - _premiumPackageItem()
 *
 * Displays premium package earning transactions.
 */
import React from 'react';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AdminUserAvatar } from './AdminUserAvatar';

interface PremiumPackageItemProps {
  transaction: AppTransactionEntity;
  currentUserId: string;
  isFromAdmin?: boolean;
}

export const PremiumPackageItem: React.FC<PremiumPackageItemProps> = ({
  transaction,
  currentUserId,
  isFromAdmin = false,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();

  const who = transaction.usersInfo.userId === currentUserId ? '' : `${transaction.usersInfo.name} `;
  const packageName = transaction.premiumPackageInfo?.packageName ?? '';
  const title = `${who}${l10n.wallet.earnedPremium} ${packageName}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.sm }}>
      <div
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AssetIcon
          path="/assets/images/premium.png"
          width={responsive.icon.medium}
          height={responsive.icon.medium}
          color={theme.exclusive}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: theme.text.primary, fontSize: responsive.font.sm, fontWeight: responsive.fontWeight.medium }}>
          {title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.xs, marginTop: responsive.gap.xs }}>
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
            {MathUtils.removeTrailingZero(transaction.premiumPackageInfo?.coins ?? 0)}
          </span>
          <AssetIcon
            path="/assets/images/coins.png"
            width={responsive.icon.small}
            height={responsive.icon.small}
            color={theme.exclusive}
          />
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
            · {dateFormatters.formatDate(transaction.createdDate, true)}
          </span>
        </div>
      </div>

      {isFromAdmin && <AdminUserAvatar userId={transaction.usersInfo.userId} />}
    </div>
  );
};
